import { Entitlement, LoadState } from "features/software/types";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Column,
  Item,
  Loading,
  Message,
  Table,
  TableHeader,
  TableWrap,
  ToolTip,
  getDate,
} from "components/Table";
import SoftwarePagination from "./SoftwarePagination";

export interface SoftwareTableProps {
  loadState: LoadState;
  dataList: Entitlement[];
  maxPage: number;
  pageIndex: number;
  transferLicense: (eId: string) => void;
  onPageChange: (selectedItem: { selected: number }) => void;
  activate: (
    pkId: string,
    eId: string,
    isTrial: boolean,
    isNodeLocked: boolean
  ) => void;
}

const SoftwareTable: React.FC<SoftwareTableProps> = (
  props: SoftwareTableProps
): JSX.Element => {
  const {
    loadState,
    dataList,
    maxPage,
    pageIndex,
    activate,
    onPageChange,
    transferLicense,
  } = props;
  const { t } = useTranslation();

  return (
    <TableWrap>
      <SoftwarePagination
        maxPage={maxPage}
        pageIndex={pageIndex}
        onPageChange={onPageChange}
        loading={loadState === "Loading"}
      />
      <Table>
        <thead>
          <TableHeader>
            <Column>
              {t("softwarePage.table.licenseNumber")}
              <ToolTip className="tooltip">
                {t("softwarePage.help.licenseNumber")}
              </ToolTip>
            </Column>
            <Column>
              {t("softwarePage.table.productName")}
              <ToolTip className="tooltip">
                {t("softwarePage.help.productName")}
              </ToolTip>
            </Column>
            <Column>
              {t("softwarePage.table.licenseModel")}
              <ToolTip className="tooltip">
                {t("softwarePage.help.licenseModel")}
              </ToolTip>
            </Column>
            <Column>
              {t("softwarePage.table.availableQuantity")}
              <ToolTip className="tooltip">
                {t("softwarePage.help.availableQuantity")}
              </ToolTip>
            </Column>
            <ButtonColumn>
              {t("softwarePage.table.activate")}
              <ToolTip className="tooltip">
                {t("softwarePage.help.activate")}
              </ToolTip>
            </ButtonColumn>
            <ButtonColumn>
              {t("softwarePage.table.transferLicense")}
              <ToolTip className="tooltip">
                {t("softwarePage.help.transferLicense")}
              </ToolTip>
            </ButtonColumn>
          </TableHeader>
        </thead>
        <tbody>
          {loadState === "Loading" && (
            <tr>
              <td colSpan={11}>
                <Loading />
              </td>
            </tr>
          )}
          {loadState === "LoadError" && (
            <tr>
              <td colSpan={11}>
                <Message style={{ color: "red" }}>
                  {t("softwarePage.message.faildGetData")}
                </Message>
              </td>
            </tr>
          )}
          {loadState === "Loaded" && dataList.length === 0 && (
            <tr>
              <td colSpan={11}>
                <Message>{t("softwarePage.message.dataEmpty")}</Message>
              </td>
            </tr>
          )}
          {loadState === "Loaded" &&
            dataList.length !== 0 &&
            dataList.map((data) => (
              <tr key={`${data.id}`}>
                <Item key={`${data.id}_id`}>{data.id}</Item>
                <Item key={`${data.id}_productName`}>{data.productName}</Item>
                <Item key={`${data.id}_licenseModel`}>{data.licenseModel}</Item>
                <Item key={`${data.id}_availableQuantity`}>
                  {data.availableQuantity} / {data.totalQuantity}
                </Item>
                <Item key={`${data.id}_activate`}>
                  <Button
                    disabled={data.availableQuantity < 1}
                    onClick={() => {
                      const disabled: boolean = data.availableQuantity < 1;
                      if (!disabled) {
                        activate(
                          data.pkId,
                          data.id,
                          data.isTrial,
                          data.isNodeLocked
                        );
                      }
                    }}
                  >
                    {t("softwarePage.button.activate")}
                  </Button>
                </Item>
                <Item key={`${data.id}_transferLicense`}>
                  <Button
                    color="red"
                    disabled={
                      data.availableQuantity < 1 ||
                      data.totalQuantity !== data.availableQuantity ||
                      data.isTrial
                    }
                    onClick={() => {
                      transferLicense(data.id);
                    }}
                  >
                    {t("softwarePage.button.transferLicense")}
                  </Button>
                </Item>
              </tr>
            ))}
        </tbody>
      </Table>
      <SoftwarePagination
        maxPage={maxPage}
        pageIndex={pageIndex}
        onPageChange={onPageChange}
        loading={loadState === "Loading"}
      />
    </TableWrap>
  );
};

export default SoftwareTable;

const ButtonColumn = styled(Column)`
  width: 10%;
`;

const Button = styled.a<{ color?: string; disabled?: boolean }>`
  display: block;
  padding: 12px 0;
  color: #ffffff;
  text-decoration: none;
  background: #005eb8;
  border: 1px #005eb8 solid;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  cursor: pointer;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
  ${({ color }) =>
    color === "red" &&
    css`
      color: #ffffff;
      background: #e50000;
      border: 1px #e50000 solid;
      :hover {
        background: rgba(229, 0, 0, 0.7);
      }
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      color: #999999;
      background: #f5f5f5;
      border: 1px #e6e6e6 solid;
      pointer-events: none;
      :hover {
        background: #fafafa;
      }
    `}
`;
